import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { TurnstileWidget } from "../../components/auth/TurnstileWidget";
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Field } from '../../components/forms/Field';
import { apiError } from "../../lib/apiError";
import {
  hasOfflineCredentials,
  isClientLoggedIn,
  migrateAwayLegacyOfflineHash,
  setSoftSessionActive,
  storeOfflineCredentials,
  tryOfflineLogin,
} from "../../lib/offline/offlineAuth";

const VERIFIED_MESSAGE = "E-mail potwierdzony. Możesz się zalogować.";
const RESET_MESSAGE = "Hasło zostało zmienione. Możesz się zalogować.";

export function LoginPage() {
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(params.get("error"));
  const [info, setInfo] = useState<string | null>(() => {
    if (params.get("verified") === "1") {
      return VERIFIED_MESSAGE;
    }
    if (params.get("reset") === "1") {
      return RESET_MESSAGE;
    }
    return null;
  });
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(false);
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [showMfaField, setShowMfaField] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [offline, setOffline] = useState(!navigator.onLine);
  const canOfflineUnlock = hasOfflineCredentials();

  useEffect(() => {
    migrateAwayLegacyOfflineHash();
    function bounceIfLoggedIn() {
      if (isClientLoggedIn()) {
        window.location.replace("/app");
      }
    }
    bounceIfLoggedIn();
    window.addEventListener("pageshow", bounceIfLoggedIn);
    return () => window.removeEventListener("pageshow", bounceIfLoggedIn);
  }, []);

  useEffect(() => {
    function syncOnline() {
      setOffline(!navigator.onLine);
    }
    window.addEventListener("online", syncOnline);
    window.addEventListener("offline", syncOnline);
    return () => {
      window.removeEventListener("online", syncOnline);
      window.removeEventListener("offline", syncOnline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.onLine) {
        return;
      }
      const result = await api.GET("/api/auth/config");
      if (cancelled || result.error) {
        return;
      }
      const data = result.data as {
        google_oauth_enabled?: boolean;
        turnstile_enabled?: boolean;
        turnstile_site_key?: string;
      };
      setGoogle(Boolean(data.google_oauth_enabled));
      setTurnstileEnabled(Boolean(data.turnstile_enabled));
      setTurnstileSiteKey(data.turnstile_site_key || "");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);

    if (!navigator.onLine) {
      const result = await tryOfflineLogin(username.trim(), password);
      setBusy(false);
      if (result.ok) {
        window.location.replace("/app");
        return;
      }
      if (result.reason === "missing") {
        setError(
          "Musisz zalogować się online przynajmniej raz (ponowne logowanie wymagane po aktualizacji zabezpieczeń).",
        );
      } else if (result.reason === "unsupported") {
        setError(
          "Logowanie offline niedostępne w tej przeglądarce — wymagane połączenie online.",
        );
      } else {
        setError("Nieprawidłowe dane logowania (tryb offline).");
      }
      return;
    }

    const result = await api.POST("/api/auth/login", {
      body: {
        username,
        password,
        captcha_token: captchaToken,
        mfa_code: mfaCode,
        remember_me: rememberMe,
      },
    });
    if (result.error) {
      setBusy(false);
      if (!showMfaField) {
        setShowMfaField(true);
      }
      setError(apiError(result.error, "Nieprawidłowy login lub hasło"));
      return;
    }
    try {
      await storeOfflineCredentials(username.trim(), password);
      setSoftSessionActive();
    } catch {
      /* offline unlock may be unavailable; session cookie still works */
    }
    setBusy(false);
    window.location.replace("/app");
  }

  return (
    <AuthLayout title="Zaloguj się">
      {offline ? (
        <p className="banner banner--info" role="status" data-testid="login-offline-info">
          Tryb offline
          {canOfflineUnlock
            ? " — zaloguj się danymi zapisanymi w pamięci urządzenia."
            : " — najpierw zaloguj się online przynajmniej raz."}
        </p>
      ) : null}
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />
      {google && !offline ? (
        <>
          <a className="btn btn--google" href="/auth/google">
            Zaloguj przez Google
          </a>
          <p className="auth__divider">lub loginem / e-mailem</p>
        </>
      ) : null}
      <form
        className="auth__form"
        data-testid="login-form"
        autoComplete="off"
        onSubmit={(event) => void onSubmit(event)}
      >
        <Field label="E-mail lub nazwa użytkownika">
          <input
            id="username"
            className="input"
            name="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            autoFocus
            autoComplete="username"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            data-testid="login-username"
          />
        </Field>
        <Field label="Hasło">
          <input
            id="password"
            className="input"
            type="password"
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            data-testid="login-password"
          />
        </Field>
        {!offline ? (
          <label className="auth__remember">
            <input
              type="checkbox"
              name="remember_me"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
              data-testid="login-remember-me"
            />
            <span>Zapamiętaj mnie (sesja 7 dni)</span>
          </label>
        ) : null}
        {!offline ? (
          <p className="auth__forgot">
            <Link to="/forgot-password" data-testid="forgot-password-link">
              Zapomniałem hasła
            </Link>
          </p>
        ) : null}
        {turnstileEnabled && turnstileSiteKey && !offline ? (
          <TurnstileWidget
            siteKey={turnstileSiteKey}
            onToken={setCaptchaToken}
            onExpire={() => setCaptchaToken("")}
          />
        ) : null}
        {showMfaField ? (
          <Field label="Kod MFA (6 cyfr)">
            <input
              id="mfa_code"
              className="input"
              name="mfa_code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9A-Za-z-]*"
              value={mfaCode}
              onChange={(event) => setMfaCode(event.target.value)}
              autoFocus={showMfaField}
              data-testid="login-mfa-code"
            />
          </Field>
        ) : null}
        <button
          className="btn btn--block"
          type="submit"
          disabled={
            busy ||
            (turnstileEnabled && !captchaToken && !offline)
          }
          data-testid="login-submit"
        >
          {offline ? "Odblokuj offline" : "Zaloguj się"}
        </button>
      </form>
      <p className="auth__footer">
        Nie masz konta? <Link to="/register">Zarejestruj się</Link>
        <br />
        <Link to="/welcome">Strona główna</Link>
      </p>
    </AuthLayout>
  );
}
